import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { noop } from 'ui-components/default-props/noop';

import { emptyArray } from 'ui-components/default-props/empty';

import { useYupFormValidation } from '../../validation/use-yup-form-validation';
import { shipFromSchema } from '../schema';
import { useEnableShipFromAddressValidation } from '../../feature-flags/hooks/use-enable-ship-from-address-validation';
import { useAddressValidation } from '@shipstation/client-common/hooks/use-address-validation';
import { AddressVerificationStatusMap, UNV, ERR } from '../../enums/address-verification-status';
import { EventSourceStrings } from '../../tracking/tracking-constants';

import {
  addNewShipFrom as addNewShipFromAction,
  editShipFrom as editShipFromAction,
  updateSellerInventoryWarehouses,
} from '../../store/seller/seller.actions';
import { clearShippyPendingPrefill } from '../../store/shippy/shippy.actions';
import { checkAddressEquality } from '../services/address-equality-service';
import { saveShipFromLtlDefaults } from '@shipstation/client-common/api/ltl.api';
import { trackShipFromConfigComplete } from '../tracking-events';
import { useShipFroms } from '../../hooks/ship-froms/use-ship-froms';
import { useShipFromRequest } from '../hooks/use-ship-from-request';
import { getInventoryWarehouses } from '../../store/inventory/inventory.api';
import { NotificationService, NOTIFICATION_TYPE_WARNING } from 'ui-components/services/notification-service';
import { shipFromContextMessages } from './ship-from-context-messages';

const ADDRESS_VALIDATION_ALLOWED_SOURCES = [EventSourceStrings.SETTINGS_SHIP_FROM_LOCATIONS, EventSourceStrings.SHIPPY_ASSISTANT];

const ShipFromContext = React.createContext(null);
export const useShipFromContext = () => useContext(ShipFromContext);

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {{ originAddress: object, returnAddress: object }} props.shipFrom
 * @param {(result: object, warehouses?: object[]) => void} props.onSuccess
 * @param {(error: object) => void} props.onError
 * @param {() => void} [props.onBack]
 * @param {(shipFrom: object) => void} [props.onShipFromBack]
 * @param {boolean} [props.shouldHideTimezone]
 * @param {() => void} [props.trackInputChange]
 * @param {() => void} [props.trackInputClick]
 * @param {Array} props.sources
 * @param {boolean} [props.isInventorySourcesLoading]
 * @param {string} [props.source]
 * @param {boolean} [props.shouldCreateInventoryWarehouse]
 * @param {object | null} [props.prefillAddress]
 */
export const ShipFromContextProvider = ({
  children,
  shipFrom,
  onSuccess,
  onError,
  onBack = noop,
  onShipFromBack = noop,
  shouldHideTimezone = false,
  trackInputChange = noop,
  trackInputClick = noop,
  sources,
  isInventorySourcesLoading = false,
  source = '',
  shouldCreateInventoryWarehouse = false,
  prefillAddress = null,
}) => {
  const intl = useIntl();
  const [contextShipFrom, setContextShipFrom] = useState(shipFrom);
  const [returnAddressSameAsShipFrom, setReturnAddressSameAsShipFrom] = useState(
    checkAddressEquality(shipFrom.originAddress, shipFrom.returnAddress),
  );
  const dispatch = useDispatch();

  const shipFromRef = useRef(shipFrom);
  shipFromRef.current = shipFrom;

  useEffect(() => {
    if (!sources.length) return;
    setContextShipFrom((prev) => {
      if (prev.inventorySourceId) return prev;
      const { inventorySourceType, inventorySourceId, inventorySourceExternalId } = shipFromRef.current;
      if (!inventorySourceId) return prev;
      return { ...prev, inventorySourceType, inventorySourceId, inventorySourceExternalId };
    });
  }, [sources]);

  const [loading, setLoading] = useAsyncSafeState(false);
  const [errors, setErrors] = useAsyncSafeState(emptyArray);
  const [showValidation, setValidationVisibility] = useAsyncSafeState(false);
  const [saveAttemptCount, setSaveAttemptCount] = useState(0);
  const isAddressValidationFlagEnabled = useEnableShipFromAddressValidation();
  const isShipFromAddressValidationEnabled = isAddressValidationFlagEnabled && ADDRESS_VALIDATION_ALLOWED_SOURCES.includes(source);
  const { validationResult, isValidating, validate, clearValidation } = useAddressValidation();
  const validationStatusCode = validationResult ? (AddressVerificationStatusMap[validationResult.status] ?? ERR) : UNV;
  const validationMessage = validationResult?.messages?.[0] ?? '';
  const suggestedAddress = validationResult?.matchedAddress ?? null;
  const validateOriginAddress = useCallback(
    (address) => {
      const countryCode = address.countryCode?.trim() || 'US';
      validate({
        company: address.company ?? '',
        line1: address.line1 ?? '',
        line2: address.line2 ?? '',
        line3: address.line3 ?? '',
        city: address.city ?? '',
        state: address.state ?? '',
        postalCode: address.postalCode ?? '',
        countryCode,
      });
    },
    [validate],
  );
  const isFirstShipFromLocation = !useShipFroms().length;
  const { errors: validationErrors, isValid } = useYupFormValidation({
    form: contextShipFrom,
    schema: shipFromSchema,
  });

  const { createShipFrom, updateShipFrom: saveShipFromUpdate } = useShipFromRequest();

  const onSave = useCallback(async () => {
    setLoading(true);
    if (!isValid) {
      setValidationVisibility(true);
      setSaveAttemptCount((c) => c + 1);
      setLoading(false);
      return;
    }
    try {
      const result = contextShipFrom.shipFromId
        ? await saveShipFromUpdate(contextShipFrom)
        : await createShipFrom(contextShipFrom, shouldCreateInventoryWarehouse);

      if (contextShipFrom.ltlDefaults && result.shipFromId) {
        try {
          await saveShipFromLtlDefaults(String(result.shipFromId), contextShipFrom.ltlDefaults);
          result.ltlDefaults = contextShipFrom.ltlDefaults;
        } catch {
          // The main ship-from record saved correctly; freight defaults failed.
          // Show a warning (not a hard error) so the user knows to retry.
          NotificationService.showNotification({
            notificationType: NOTIFICATION_TYPE_WARNING,
            content: intl.formatMessage(shipFromContextMessages.ltlDefaultsPartialSaveWarning),
          });
        }
      }

      // Dispatch after the ltlDefaults enrichment so the seller store copy carries them
      // (use-ship-from-resolution prefill reads ltlDefaults off this store list).
      if (contextShipFrom.shipFromId) {
        dispatch(editShipFromAction(result));
      } else {
        dispatch(addNewShipFromAction(result));
      }

      trackShipFromConfigComplete(result, isFirstShipFromLocation, source);
      if (shouldCreateInventoryWarehouse) {
        getInventoryWarehouses().promise.then((response) => {
          const updateWarehouses = response?.data || [];
          dispatch(updateSellerInventoryWarehouses(updateWarehouses));
          onSuccess(result, updateWarehouses);
        });
      } else {
        onSuccess(result);
      }
    } catch (err) {
      setErrors(err.Errors || emptyArray);
      onError(err);
    }
    setLoading(false);
  }, [
    setLoading,
    contextShipFrom,
    isValid,
    setValidationVisibility,
    createShipFrom,
    saveShipFromUpdate,
    dispatch,
    isFirstShipFromLocation,
    source,
    onSuccess,
    setErrors,
    onError,
    shouldCreateInventoryWarehouse,
    intl,
  ]);

  const onSaveDraft = useCallback(() => {
    onShipFromBack(contextShipFrom);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [contextShipFrom, onBack]);

  const updateShipFrom = useCallback(
    (updatedProperties) => {
      if ('originAddress' in updatedProperties) {
        clearValidation();
      }
      setContextShipFrom((prevState) => ({
        ...prevState,
        ...updatedProperties,
      }));
    },
    [setContextShipFrom, clearValidation],
  );

  useEffect(() => {
    if (!prefillAddress || Object.keys(prefillAddress).length === 0) return;

    setContextShipFrom((prev) => {
      const newOrigin = {
        countryCode: prev.originAddress.countryCode,
        ianaTimeZone: prev.originAddress.ianaTimeZone,
        ...prefillAddress,
      };
      const updatedShipFrom = { ...prev, originAddress: newOrigin };
      if (returnAddressSameAsShipFrom) {
        updatedShipFrom.returnAddress = newOrigin;
      }
      return updatedShipFrom;
    });

    dispatch(clearShippyPendingPrefill());
  }, [prefillAddress, returnAddressSameAsShipFrom, dispatch]);

  const value = useMemo(() => {
    return {
      shipFrom: contextShipFrom,
      updateShipFrom,
      validationErrors,
      isValid,
      returnAddressSameAsShipFrom,
      setReturnAddressSameAsShipFrom,
      onSave,
      onSaveDraft,
      showValidation,
      saveAttemptCount,
      errors,
      setErrors,
      shouldHideTimezone,
      trackInputChange,
      trackInputClick,
      sources,
      isInventorySourcesLoading,
      loading,
      isShipFromAddressValidationEnabled,
      validationStatusCode,
      validationMessage,
      suggestedAddress,
      isValidating,
      validateOriginAddress,
    };
  }, [
    contextShipFrom,
    updateShipFrom,
    validationErrors,
    isValid,
    returnAddressSameAsShipFrom,
    onSave,
    onSaveDraft,
    showValidation,
    saveAttemptCount,
    errors,
    setErrors,
    shouldHideTimezone,
    trackInputChange,
    trackInputClick,
    sources,
    isInventorySourcesLoading,
    loading,
    isShipFromAddressValidationEnabled,
    validationStatusCode,
    validationMessage,
    suggestedAddress,
    isValidating,
    validateOriginAddress,
  ]);

  return <ShipFromContext.Provider value={value}>{children}</ShipFromContext.Provider>;
};
